import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useVendorAuth } from '../contexts/VendorAuthContext';

export const useTokenValidation = () => {
  const [searchParams] = useSearchParams();
  const { validateToken, isAuthenticated, isLoading } = useVendorAuth();
  const [isValidating, setIsValidating] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [hasValidated, setHasValidated] = useState(false);

  useEffect(() => {
    const token = searchParams.get('token');
    
    // If no token in URL and user is not authenticated, redirect to error page
    if (!token && !isAuthenticated) {
      setValidationError('No token provided. Please use the link from your approval email.');
      return;
    }

    // If user is already authenticated, no need to validate token again
    if (isAuthenticated) {
      return;
    }

    // If token exists and user is not authenticated, validate the token
    if (token && !isAuthenticated && !isValidating && !hasValidated) {
      setIsValidating(true);
      setValidationError(null);
      setHasValidated(true);
      
      validateToken(token)
        .then((isValid) => {
          if (!isValid) {
            // Don't set validation error here - the context already shows the error toast
            setValidationError('Token validation failed');
          }
          // If valid, the context will handle setting the vendor session
        })
        .catch((error) => {
          console.error('Token validation failed:', error);
          // Don't show duplicate error - the context already handles error toasts
          setValidationError('Token validation failed');
        })
        .finally(() => {
          setIsValidating(false);
        });
    }
  }, [searchParams, isAuthenticated, validateToken, isValidating, hasValidated]);

  // Clean up URL by removing token parameter after successful validation
  useEffect(() => {
    if (isAuthenticated && searchParams.get('token')) {
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('token');
      const newUrl = `${window.location.pathname}${newSearchParams.toString() ? `?${newSearchParams.toString()}` : ''}`;
      window.history.replaceState({}, '', newUrl);
    }
  }, [isAuthenticated, searchParams]);

  return {
    isAuthenticated,
    isLoading: isLoading || isValidating,
    validationError,
    hasToken: !!searchParams.get('token'),
    hasValidated
  };
};
